import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const MIN_PASSWORD = 12;

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [fullName, setFullName] = useState('');
  const [salutationName, setSalutationName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    document.title = 'Accept invitation - Brookline Notepad';
  }, []);

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  useEffect(() => {
    let active = true;
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`/api/auth/invite/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!active) return;
        if (!res.ok) {
          setError(data.error || 'This invitation is invalid or expired.');
        } else {
          setInvite(data);
          setFullName(data.full_name || '');
          setSalutationName(data.salutation_name || '');
        }
      } catch {
        if (active) setError('Network error. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await acceptInvite(token, password, fullName, salutationName);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <a href="#main-content" className="auth-skip-link">Skip to main content</a>
      <Header title="Brookline Notepad" />

      <main id="main-content" tabIndex={-1} className="auth-content">
        <div className="auth-card">
          <h2 className="auth-title">Accept your invitation</h2>

          {loading ? (
            <p className="auth-subtitle" role="status">Loading your invitation…</p>
          ) : !invite ? (
            <>
              <div className="auth-error" role="alert" tabIndex={-1} ref={errorRef}>
                {error || 'This invitation link is invalid, already used, or expired.'}
              </div>
              <button className="auth-link-btn" onClick={() => navigate('/signin')}>
                Go to Sign in
              </button>
            </>
          ) : (
            <>
              <p className="auth-subtitle">
                You’re creating an account for <strong>{invite.email}</strong>.
                Choose a password to finish.
              </p>
              <form onSubmit={submit} className="auth-form" noValidate>
                <div className="auth-field">
                  <label htmlFor="inv-full-name">Your Name</label>
                  <input
                    id="inv-full-name"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="inv-salutation">I&rsquo;ll call you&hellip;</label>
                  <input
                    id="inv-salutation"
                    type="text"
                    value={salutationName}
                    onChange={(e) => setSalutationName(e.target.value)}
                    autoComplete="given-name"
                  />
                </div>
                <PasswordInput
                  id="inv-password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD}
                  describedBy="inv-password-help"
                />
                <p id="inv-password-help" className="auth-help">
                  At least {MIN_PASSWORD} characters.
                </p>
                <PasswordInput
                  id="inv-confirm-password"
                  label="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD}
                />
                {confirmPassword.length > 0 && (
                  <p
                    className={password === confirmPassword ? 'auth-status' : 'auth-mismatch'}
                    role="status"
                    aria-live="polite"
                  >
                    {password === confirmPassword ? 'Passwords match.' : 'Passwords do not match.'}
                  </p>
                )}

                {error && (
                  <div className="auth-error" role="alert" tabIndex={-1} ref={errorRef}>
                    {error}
                  </div>
                )}

                <button type="submit" className="auth-btn" disabled={busy}>
                  {busy ? 'Creating account…' : 'Create account'}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default AcceptInvite;
